import { useEffect, useCallback, useState, useRef } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';
import { ApiResult, useService } from '../api';
import { useCampaign } from '../campaign';
import { useExperiment } from '../experiments/useExperiment';
import {
  SKIP_CUSTOMER_INFO,
  CHECKOUT_NEXT_BOX_MVP,
  CHECKOUT_GWP_CALLOUT,
} from '../experiments/constants';
import { ShopifyCartService } from './services';

export const useShopifyCart = ({
  shouldCreateCart = true,
  hasCheckedPath = false,
} = {}) => {
  const service = useService(ShopifyCartService);
  const { campaignId } = useCampaign();
  const [cartId, setCartId] = useState(null);
  const campaignIdProcessingRef = useRef(false);
  const [addedAttributes, setAddedAttributes] = useState({});
  const creatingRef = useRef(null);
  const cartIdRef = useRef(cartId);
  const { value: skipCustomerInfo } = useExperiment(SKIP_CUSTOMER_INFO, false);
  const { value: showCheckoutNextBoxMVP } = useExperiment(
    CHECKOUT_NEXT_BOX_MVP,
    false,
  );
  const { value: showCheckoutGWPCallout } = useExperiment(
    CHECKOUT_GWP_CALLOUT,
    false,
  );

  // Keep cartIdRef.current updated with the latest cartId
  cartIdRef.current = cartId;

  const updateCartId = useCallback(
    newId => {
      if (newId && newId !== cartId) {
        setCartId(newId);
        Cookies.set('shopifyCartId', newId, { path: '/', expires: 30 });
      }
    },
    [cartId],
  );

  const createCart = useCallback(async () => {
    if (cartIdRef.current) return { cart_id: cartIdRef.current };

    //* If a creation is already in progress, return the existing promise
    if (creatingRef.current) {
      return creatingRef.current;
    }

    creatingRef.current = (async () => {
      try {
        const response = await service.createCart();

        if (response?.data?.cart_id) {
          updateCartId(response.data.cart_id);
          return response.data;
        } else {
          return response?.data || null;
        }
      } catch (error) {
        creatingRef.current = null;
        return null;
      }
    })();

    return creatingRef.current;
  }, [service, updateCartId]);

  const {
    data: cartData,
    error: cartError,
    isLoading: isShopifyCartLoading,
    mutate: refreshCart,
  } = useSWR(cartId ? `/api/v4/onboarding/${cartId}/` : null, null, {
    revalidateOnFocus: false,
    dedupingInterval: 10000,
    onSuccess: async data => {
      const hasOnboardingAttributes =
        'order_type' in addedAttributes && 'cart_id' in addedAttributes;

      if (hasOnboardingAttributes) return;

      const flags = {
        skipCustomerInfo,
        showCheckoutNextBoxMVP,
        showCheckoutGWPCallout,
      };

      const abTestFlags = Object.entries(flags)
        .map(([key, value]) => `${key}:${value}`)
        .join(',');

      const attributes = {
        order_type: 'onboarding',
        cart_id: data.cart_id,
        ab_tests: abTestFlags,
      };

      await updateCartAttributes(attributes, data.cart_id);

      setAddedAttributes({
        ...addedAttributes,
        ...attributes,
      });
    },
    onError: async error => {
      if (error.response?.status === 404) {
        Cookies.remove('shopifyCartId', { path: '/' });
        setCartId(null);
        if (shouldCreateCart) {
          await createCart();
        }
      }
    },
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (retryCount >= 1) return;
      setTimeout(() => revalidate({ retryCount }), 1000);
    },
  });

  const addPetData = useCallback(
    async (petData = {}) => {
      let currentCartId = cartIdRef.current; // potential race condition handling with ref

      // If no cartId, try to create one
      if (!currentCartId) {
        const newCart = await createCart();
        currentCartId = newCart?.cart_id;
      }

      if (!currentCartId) {
        return null;
      }

      try {
        const response = await service.addPetData(currentCartId, {
          ...petData,
        });

        if (!currentCartId) {
          return null;
        }

        await refreshCart(response.data, false);
        return response?.data;
      } catch (error) {
        throw error;
      }
    },
    [service, refreshCart, createCart],
  );

  const attachCustomer = useCallback(
    async (customerData = {}) => {
      if (!cartId) return null;
      if (!customerData.email) return null;

      try {
        const response = await service.attachCustomer(cartId, customerData);
        await refreshCart(response.data, false);
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    [service, cartId, refreshCart],
  );

  const deletePetFromCart = useCallback(
    async petId => {
      if (!cartId) return null;

      const response = await service.removePet(cartId, petId);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, refreshCart],
  );

  const addPromo = useCallback(
    async promoCode => {
      if (!cartId || cartError) return null;

      const response = await service.addPromo(cartId, promoCode);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, cartError, refreshCart],
  );

  const upsertMealPlan = useCallback(
    async (petId, planData = {}) => {
      if (!cartId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.upsertMealPlan(cartId, petId, planData);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const attachExtras = useCallback(
    async (petId, extrasData = []) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachExtras(cartId, petId, extrasData);
        refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const updateCartAttributes = useCallback(
    async (attributes = {}, idOverride = null) => {
      const id = idOverride || cartId;

      if (!id) {
        // eslint-disable-next-line no-console
        console.warn('updateCartAttributes: No Cart ID available');
        return null;
      }

      return ApiResult.callAsync(async () => {
        const response = await service.updateAttributes(id, attributes);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  useEffect(() => {
    //* I found that the createCart call being awaited was better for adding the attribute following.
    const initCart = async () => {
      const savedCartId = Cookies.get('shopifyCartId');

      if (savedCartId) {
        setCartId(savedCartId);
      } else if (shouldCreateCart) {
        try {
          await createCart();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize cart:', error);
        }
      }
    };

    initCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreateCart]);

  //* Update cart attributes with campaign_id & cart_id when cart is first created
  useEffect(() => {
    if (
      campaignIdProcessingRef.current ||
      !cartId ||
      !campaignId ||
      Object.keys(addedAttributes).length === 0 ||
      'campaign_id' in addedAttributes
    )
      return;
    campaignIdProcessingRef.current = true;
    const updateCampaign = async campaignId => {
      await new Promise(res => setTimeout(res, 1000)); // debounce this so any other requests can resolve
      await updateCartAttributes({
        campaign_id: campaignId,
        ...addedAttributes, // Don't risk overwriting anything
      });
      setAddedAttributes({ ...addedAttributes, campaign_id: campaignId });
      campaignIdProcessingRef.current = false;
    };
    updateCampaign(campaignId);
  }, [
    cartId,
    updateCartAttributes,
    setAddedAttributes,
    campaignId,
    addedAttributes,
  ]);

  const getCouponCode = useCallback(() => {
    if (!cartData?.coupon_code?.length) return null;
    return cartData.coupon_code[0];
  }, [cartData]);
  const isLoading =
    !hasCheckedPath ||
    isShopifyCartLoading ||
    (shouldCreateCart && !cartData && !cartError);

  return {
    cartId,
    cart: cartData || {},
    error: cartError,
    isLoading,
    createCart,
    addPetData,
    attachCustomer,
    deletePetFromCart,
    addPromo,
    upsertMealPlan,
    attachExtras,
    refreshCart,
    updateCartAttributes,
    couponCode: getCouponCode(),
  };
};
